import {
  useRef,
  useState,
  useEffect,
  useContext,
  useLayoutEffect,
} from "react";
import { IconButton, Dialog, DialogType, Stack } from "@fluentui/react";
import { SquareRegular, ShieldLockRegular } from "@fluentui/react-icons";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import uuid from "react-uuid";

import styles from "./Chat.module.css";
import CancelIcon from "../../assets/cancel.svg";
import Refresh from "../../assets/Refresh.svg";
import Jassim from "../../assets/sanad.png";
import QDBLogoChat from "../../assets/QDBLogoChat.svg";

import {
  ChatMessage,
  ConversationRequest,
  conversationApi,
  Citation,
  ToolMessageContent,
  ChatResponse,
  getUserInfo,
  Conversation,
  historyGenerate,
  historyUpdate,
  historyClear,
  ChatHistoryLoadingState,
  CosmosDBStatus,
  ErrorMessage,
  recognizeApi,
  getSpeechToken,
} from "../../api";
import { Answer } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { ChatHistoryPanel } from "../../components/ChatHistory/ChatHistoryPanel";
import { AppStateContext } from "../../state/AppProvider";
import { useBoolean } from "@fluentui/react-hooks";
import DOMPurify from "dompurify";
import { XSSAllowTags } from "../../constants/xssAllowtags";
import { isEmpty } from "lodash";
import { removeDocumentReferenceFromString } from "../../utils/general";
import SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import { useNavigate } from "react-router-dom";

const enum messageStatus {
  NotRunning = "Not Running",
  Processing = "Processing",
  Done = "Done",
}

const [ASSISTANT, TOOL, ERROR] = ["assistant", "tool", "error"];
const NO_CONTENT_ERROR = "No content in messages object.";

const Chat = () => {
  const [synthesizer, setSynthesizer] =
    useState<SpeechSDK.SpeechSynthesizer | null>(null);
  const synthesizerRef = useRef<SpeechSDK.SpeechSynthesizer | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);

  const navigate = useNavigate();

  const handleCloseChat = () => {
    newChat();
    navigate("/main"); // Navigate to the Chat route
  };
  const appStateContext = useContext(AppStateContext);
  const ui = appStateContext?.state.frontendSettings?.ui;
  const AUTH_ENABLED = appStateContext?.state.frontendSettings?.auth_enabled;
  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false);
  const [activeCitation, setActiveCitation] = useState<Citation>();
  const [isCitationPanelOpen, setIsCitationPanelOpen] =
    useState<boolean>(false);
  const abortFuncs = useRef([] as AbortController[]);
  const [showAuthMessage, setShowAuthMessage] = useState<boolean | undefined>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [processMessages, setProcessMessages] = useState<messageStatus>(
    messageStatus.NotRunning
  );
  const [clearingChat, setClearingChat] = useState<boolean>(false);
  const [hideErrorDialog, { toggle: toggleErrorDialog }] = useBoolean(true);
  const [errorMsg, setErrorMsg] = useState<ErrorMessage | null>();

  const errorDialogContentProps = {
    type: DialogType.close,
    title: errorMsg?.title,
    closeButtonAriaLabel: "Close",
    subText: errorMsg?.subtitle,
  };

  const modalProps = {
    titleAriaId: "labelId",
    subtitleAriaId: "subTextId",
    isBlocking: true,
    styles: { main: { maxWidth: 450 } },
  };

  const [ASSISTANT, TOOL, ERROR] = ["assistant", "tool", "error"];
  const NO_CONTENT_ERROR = "No content in messages object.";

  useEffect(() => {
    if (
      appStateContext?.state.isCosmosDBAvailable?.status !==
        CosmosDBStatus.Working &&
      appStateContext?.state.isCosmosDBAvailable?.status !==
        CosmosDBStatus.NotConfigured &&
      appStateContext?.state.chatHistoryLoadingState ===
        ChatHistoryLoadingState.Fail &&
      hideErrorDialog
    ) {
      let subtitle = `${appStateContext.state.isCosmosDBAvailable.status}. Please contact the site administrator.`;
      setErrorMsg({
        title: "Chat history is not enabled",
        subtitle: subtitle,
      });
      toggleErrorDialog();
    }
  }, [appStateContext?.state.isCosmosDBAvailable]);

  const handleErrorDialogClose = () => {
    toggleErrorDialog();
    setTimeout(() => {
      setErrorMsg(null);
    }, 500);
  };

  useEffect(() => {
    setIsLoading(
      appStateContext?.state.chatHistoryLoadingState ===
        ChatHistoryLoadingState.Loading
    );
  }, [appStateContext?.state.chatHistoryLoadingState]);

  const getUserInfoList = async () => {
    if (!AUTH_ENABLED) {
      setShowAuthMessage(false);
      return;
    }
    const userInfoList = await getUserInfo();
    if (userInfoList.length === 0 && window.location.hostname !== "127.0.0.1") {
      setShowAuthMessage(true);
    } else {
      setShowAuthMessage(false);
    }
  };

  let assistantMessage = {} as ChatMessage;
  let toolMessage = {} as ChatMessage;
  let assistantContent = "";

  const processResultMessage = (
    resultMessage: ChatMessage,
    userMessage: ChatMessage,
    conversationId?: string
  ) => {
    if (resultMessage.role === ASSISTANT) {
      assistantContent += removeDocumentReferenceFromString(
        resultMessage.content
      );
      assistantMessage = resultMessage;
      assistantMessage.content = assistantContent;

      if (resultMessage.context) {
        toolMessage = {
          id: uuid(),
          role: TOOL,
          content: resultMessage.context,
          date: new Date().toISOString(),
        };
      }
    }

    if (resultMessage.role === TOOL) toolMessage = resultMessage;

    if (!conversationId) {
      isEmpty(toolMessage)
        ? setMessages([...messages, userMessage, assistantMessage])
        : setMessages([
            ...messages,
            userMessage,
            toolMessage,
            assistantMessage,
          ]);
    } else {
      isEmpty(toolMessage)
        ? setMessages([...messages, assistantMessage])
        : setMessages([...messages, toolMessage, assistantMessage]);
    }
  };

  const updateConversation = (conversation: Conversation) => {
    appStateContext?.dispatch({
      type: "UPDATE_CURRENT_CHAT",
      payload: conversation,
    });
    setMessages([...conversation.messages]);
  };

  const addMessageToConversation = (
    conversation: Conversation | null | undefined,
    message: ChatMessage
  ) => {
    if (!conversation) {
      conversation = {
        id: uuid(),
        title: message.content,
        messages: [],
        date: new Date().toISOString(),
      };
    }

    if (!conversation.messages.includes(message)) {
      conversation.messages.push(message);
      updateConversation(conversation);
    }
  };

  const makeApiRequestWithoutCosmosDB = async (
    question: string,
    conversationId?: string
  ) => {
    setIsLoading(true);
    setShowLoadingMessage(true);
    const abortController = new AbortController();
    abortFuncs.current.unshift(abortController);

    const userMessage: ChatMessage = {
      id: uuid(),
      role: "user",
      content: question,
      date: new Date().toISOString(),
    };

    let conversation: Conversation | null | undefined = conversationId
      ? appStateContext?.state?.currentChat
      : {
          id: conversationId ?? uuid(),
          title: question,
          messages: [],
          date: new Date().toISOString(),
        };

    if (!conversation) {
      console.error("Conversation not found.");
      setIsLoading(false);
      setShowLoadingMessage(false);
      abortFuncs.current = abortFuncs.current.filter(
        (a) => a !== abortController
      );
      return;
    }

    addMessageToConversation(conversation, userMessage);

    const request: ConversationRequest = {
      messages: conversation.messages.filter((msg) => msg.role !== ERROR),
    };

    let result = {} as ChatResponse;
    try {
      const response = await conversationApi(request, abortController.signal);
      if (response?.body) {
        const reader = response.body.getReader();
        let runningText = "";

        while (true) {
          setProcessMessages(messageStatus.Processing);
          const { done, value } = await reader.read();
          if (done) break;

          const text = new TextDecoder("utf-8").decode(value);
          const objects = text.split("\n");

          objects.forEach((obj) => {
            try {
              if (obj && obj !== "{}") {
                runningText += obj;
                result = JSON.parse(runningText);

                if (result.choices?.length > 0) {
                  result.choices[0].messages.forEach((msg) => {
                    msg.id = result.id;
                    msg.date = new Date().toISOString();
                  });

                  if (
                    result.choices[0].messages.some((m) => m.role === ASSISTANT)
                  ) {
                    setShowLoadingMessage(false);
                  }

                  result.choices[0].messages.forEach((msg) => {
                    processResultMessage(msg, userMessage, conversationId);
                  });
                } else if (result.error) {
                  throw Error(result.error);
                }
                runningText = "";
              }
            } catch (e) {
              if (!(e instanceof SyntaxError)) {
                console.error(e);
                throw e;
              } else {
                console.log("Incomplete message. Continuing...");
              }
            }
          });
        }

        conversation.messages.push(toolMessage, assistantMessage);
        updateConversation(conversation);
      }
    } catch (e) {
      if (!abortController.signal.aborted) {
        let errorMessage =
          result.error?.message ||
          (typeof result.error === "string"
            ? result.error
            : "An error occurred. Please try again. If the problem persists, please contact the site administrator.");

        const errorChatMsg: ChatMessage = {
          id: uuid(),
          role: ERROR,
          content: errorMessage,
          date: new Date().toISOString(),
        };

        addMessageToConversation(conversation, errorChatMsg);
      } else {
        setMessages([...messages, userMessage]);
      }
    } finally {
      setIsLoading(false);
      setShowLoadingMessage(false);
      abortFuncs.current = abortFuncs.current.filter(
        (a) => a !== abortController
      );
      setProcessMessages(messageStatus.Done);
    }

    return abortController.abort();
  };

  // const makeApiRequestWithoutCosmosDB = async (
  //   question: string,
  //   conversationId?: string
  // ) => {
  //   setIsLoading(true);
  //   setShowLoadingMessage(true);
  //   const abortController = new AbortController();
  //   abortFuncs.current.unshift(abortController);

  //   const userMessage: ChatMessage = {
  //     id: uuid(),
  //     role: "user",
  //     content: question,
  //     date: new Date().toISOString(),
  //   };

  //   let conversation: Conversation | null | undefined;
  //   if (!conversationId) {
  //     conversation = {
  //       id: conversationId ?? uuid(),
  //       title: question,
  //       messages: [userMessage],
  //       date: new Date().toISOString(),
  //     };
  //   } else {
  //     conversation = appStateContext?.state?.currentChat;
  //     if (!conversation) {
  //       console.error("Conversation not found.");
  //       setIsLoading(false);
  //       setShowLoadingMessage(false);
  //       abortFuncs.current = abortFuncs.current.filter(
  //         (a) => a !== abortController
  //       );
  //       return;
  //     } else {
  //       conversation.messages.push(userMessage);
  //     }
  //   }

  //   appStateContext?.dispatch({
  //     type: "UPDATE_CURRENT_CHAT",
  //     payload: conversation,
  //   });
  //   setMessages(conversation.messages);

  //   const request: ConversationRequest = {
  //     messages: [
  //       ...conversation.messages.filter((answer) => answer.role !== ERROR),
  //     ],
  //   };

  //   let result = {} as ChatResponse;
  //   try {
  //     const response = await conversationApi(request, abortController.signal);
  //     if (response?.body) {
  //       const reader = response.body.getReader();

  //       let runningText = "";
  //       while (true) {
  //         setProcessMessages(messageStatus.Processing);
  //         const { done, value } = await reader.read();
  //         if (done) break;

  //         var text = new TextDecoder("utf-8").decode(value);
  //         const objects = text.split("\n");
  //         objects.forEach((obj) => {
  //           try {
  //             if (obj !== "" && obj !== "{}") {
  //               runningText += obj;
  //               result = JSON.parse(runningText);
  //               if (result.choices?.length > 0) {
  //                 result.choices[0].messages.forEach((msg) => {
  //                   msg.id = result.id;
  //                   msg.date = new Date().toISOString();
  //                 });
  //                 if (
  //                   result.choices[0].messages?.some(
  //                     (m) => m.role === ASSISTANT
  //                   )
  //                 ) {
  //                   setShowLoadingMessage(false);
  //                 }
  //                 result.choices[0].messages.forEach((resultObj) => {
  //                   processResultMessage(
  //                     resultObj,
  //                     userMessage,
  //                     conversationId
  //                   );
  //                 });
  //               } else if (result.error) {
  //                 throw Error(result.error);
  //               }
  //               runningText = "";
  //             }
  //           } catch (e) {
  //             if (!(e instanceof SyntaxError)) {
  //               console.error(e);
  //               throw e;
  //             } else {
  //               console.log("Incomplete message. Continuing...");
  //             }
  //           }
  //         });
  //       }
  //       conversation.messages.push(toolMessage, assistantMessage);
  //       appStateContext?.dispatch({
  //         type: "UPDATE_CURRENT_CHAT",
  //         payload: conversation,
  //       });
  //       setMessages([...messages, toolMessage, assistantMessage]);
  //     }
  //   } catch (e) {
  //     if (!abortController.signal.aborted) {
  //       let errorMessage =
  //         "An error occurred. Please try again. If the problem persists, please contact the site administrator.";
  //       if (result.error?.message) {
  //         errorMessage = result.error.message;
  //       } else if (typeof result.error === "string") {
  //         errorMessage = result.error;
  //       }
  //       let errorChatMsg: ChatMessage = {
  //         id: uuid(),
  //         role: ERROR,
  //         content: errorMessage,
  //         date: new Date().toISOString(),
  //       };
  //       conversation.messages.push(errorChatMsg);
  //       appStateContext?.dispatch({
  //         type: "UPDATE_CURRENT_CHAT",
  //         payload: conversation,
  //       });
  //       setMessages([...messages, errorChatMsg]);
  //     } else {
  //       setMessages([...messages, userMessage]);
  //     }
  //   } finally {
  //     setIsLoading(false);
  //     setShowLoadingMessage(false);
  //     abortFuncs.current = abortFuncs.current.filter(
  //       (a) => a !== abortController
  //     );
  //     setProcessMessages(messageStatus.Done);
  //   }

  //   return abortController.abort();
  // };

  const makeApiRequestWithCosmosDB = async (
    question: string,
    conversationId?: string
  ) => {
    setIsLoading(true);
    setShowLoadingMessage(true);
    const abortController = new AbortController();
    abortFuncs.current.unshift(abortController);

    const userMessage: ChatMessage = {
      id: uuid(),
      role: "user",
      content: question,
      date: new Date().toISOString(),
    };

    //api call params set here (generate)
    let request: ConversationRequest;
    let conversation;
    if (conversationId) {
      conversation = appStateContext?.state?.chatHistory?.find(
        (conv) => conv.id === conversationId
      );
      if (!conversation) {
        console.error("Conversation not found.");
        setIsLoading(false);
        setShowLoadingMessage(false);
        abortFuncs.current = abortFuncs.current.filter(
          (a) => a !== abortController
        );
        return;
      } else {
        conversation.messages.push(userMessage);
        request = {
          messages: [
            ...conversation.messages.filter((answer) => answer.role !== ERROR),
          ],
        };
      }
    } else {
      request = {
        messages: [userMessage].filter((answer) => answer.role !== ERROR),
      };
      setMessages(request.messages);
    }
    let result = {} as ChatResponse;
    try {
      const response = conversationId
        ? await historyGenerate(request, abortController.signal, conversationId)
        : await historyGenerate(request, abortController.signal);
      if (!response?.ok) {
        const responseJson = await response.json();
        var errorResponseMessage =
          responseJson.error === undefined
            ? "Please try again. If the problem persists, please contact the site administrator."
            : responseJson.error;
        let errorChatMsg: ChatMessage = {
          id: uuid(),
          role: ERROR,
          content: `There was an error generating a response. Chat history can't be saved at this time. ${errorResponseMessage}`,
          date: new Date().toISOString(),
        };
        let resultConversation;
        if (conversationId) {
          resultConversation = appStateContext?.state?.chatHistory?.find(
            (conv) => conv.id === conversationId
          );
          if (!resultConversation) {
            console.error("Conversation not found.");
            setIsLoading(false);
            setShowLoadingMessage(false);
            abortFuncs.current = abortFuncs.current.filter(
              (a) => a !== abortController
            );
            return;
          }
          resultConversation.messages.push(errorChatMsg);
        } else {
          setMessages([...messages, userMessage, errorChatMsg]);
          setIsLoading(false);
          setShowLoadingMessage(false);
          abortFuncs.current = abortFuncs.current.filter(
            (a) => a !== abortController
          );
          return;
        }
        appStateContext?.dispatch({
          type: "UPDATE_CURRENT_CHAT",
          payload: resultConversation,
        });
        setMessages([...resultConversation.messages]);
        return;
      }
      if (response?.body) {
        const reader = response.body.getReader();

        let runningText = "";
        while (true) {
          setProcessMessages(messageStatus.Processing);
          const { done, value } = await reader.read();
          if (done) break;

          var text = new TextDecoder("utf-8").decode(value);
          const objects = text.split("\n");
          objects.forEach((obj) => {
            try {
              if (obj !== "" && obj !== "{}") {
                runningText += obj;
                result = JSON.parse(runningText);
                if (!result.choices?.[0]?.messages?.[0].content) {
                  errorResponseMessage = NO_CONTENT_ERROR;
                  throw Error();
                }
                if (result.choices?.length > 0) {
                  result.choices[0].messages.forEach((msg) => {
                    msg.id = result.id;
                    msg.date = new Date().toISOString();
                  });
                  if (
                    result.choices[0].messages?.some(
                      (m) => m.role === ASSISTANT
                    )
                  ) {
                    setShowLoadingMessage(false);
                  }
                  result.choices[0].messages.forEach((resultObj) => {
                    processResultMessage(
                      resultObj,
                      userMessage,
                      conversationId
                    );
                  });
                }
                runningText = "";
              } else if (result.error) {
                throw Error(result.error);
              }
            } catch (e) {
              if (!(e instanceof SyntaxError)) {
                console.error(e);
                throw e;
              } else {
                console.log("Incomplete message. Continuing...");
              }
            }
          });
        }

        let resultConversation;
        if (conversationId) {
          resultConversation = appStateContext?.state?.chatHistory?.find(
            (conv) => conv.id === conversationId
          );
          if (!resultConversation) {
            console.error("Conversation not found.");
            setIsLoading(false);
            setShowLoadingMessage(false);
            abortFuncs.current = abortFuncs.current.filter(
              (a) => a !== abortController
            );
            return;
          }
          isEmpty(toolMessage)
            ? resultConversation.messages.push(assistantMessage)
            : resultConversation.messages.push(toolMessage, assistantMessage);
        } else {
          resultConversation = {
            id: result.history_metadata.conversation_id,
            title: result.history_metadata.title,
            messages: [userMessage],
            date: result.history_metadata.date,
          };
          isEmpty(toolMessage)
            ? resultConversation.messages.push(assistantMessage)
            : resultConversation.messages.push(toolMessage, assistantMessage);
        }
        if (!resultConversation) {
          setIsLoading(false);
          setShowLoadingMessage(false);
          abortFuncs.current = abortFuncs.current.filter(
            (a) => a !== abortController
          );
          return;
        }
        appStateContext?.dispatch({
          type: "UPDATE_CURRENT_CHAT",
          payload: resultConversation,
        });
        // If this was a brand-new conversation it only exists in currentChat so
        // far.  Add it to chatHistory now so subsequent messages can find it by
        // ID in the chatHistory lookup above.
        if (!conversationId) {
          appStateContext?.dispatch({
            type: "UPDATE_CHAT_HISTORY",
            payload: resultConversation,
          });
        }
        isEmpty(toolMessage)
          ? setMessages([...messages, assistantMessage])
          : setMessages([...messages, toolMessage, assistantMessage]);
      }
    } catch (e) {
      if (!abortController.signal.aborted) {
        let errorMessage = `An error occurred. ${errorResponseMessage}`;
        if (result.error?.message) {
          errorMessage = result.error.message;
        } else if (typeof result.error === "string") {
          errorMessage = result.error;
        }
        let errorChatMsg: ChatMessage = {
          id: uuid(),
          role: ERROR,
          content: errorMessage,
          date: new Date().toISOString(),
        };
        let resultConversation;
        if (conversationId) {
          resultConversation = appStateContext?.state?.chatHistory?.find(
            (conv) => conv.id === conversationId
          );
          if (!resultConversation) {
            console.error("Conversation not found.");
            setIsLoading(false);
            setShowLoadingMessage(false);
            abortFuncs.current = abortFuncs.current.filter(
              (a) => a !== abortController
            );
            return;
          }
          resultConversation.messages.push(errorChatMsg);
        } else {
          if (!result.history_metadata) {
            console.error("Error retrieving data.", result);
            let errorChatMsg: ChatMessage = {
              id: uuid(),
              role: ERROR,
              content: errorMessage,
              date: new Date().toISOString(),
            };
            setMessages([...messages, userMessage, errorChatMsg]);
            setIsLoading(false);
            setShowLoadingMessage(false);
            abortFuncs.current = abortFuncs.current.filter(
              (a) => a !== abortController
            );
            return;
          }
          resultConversation = {
            id: result.history_metadata.conversation_id,
            title: result.history_metadata.title,
            messages: [userMessage],
            date: result.history_metadata.date,
          };
          resultConversation.messages.push(errorChatMsg);
        }
        if (!resultConversation) {
          setIsLoading(false);
          setShowLoadingMessage(false);
          abortFuncs.current = abortFuncs.current.filter(
            (a) => a !== abortController
          );
          return;
        }
        appStateContext?.dispatch({
          type: "UPDATE_CURRENT_CHAT",
          payload: resultConversation,
        });
        setMessages([...messages, errorChatMsg]);
      } else {
        setMessages([...messages, userMessage]);
      }
    } finally {
      setIsLoading(false);
      setShowLoadingMessage(false);
      abortFuncs.current = abortFuncs.current.filter(
        (a) => a !== abortController
      );
      setProcessMessages(messageStatus.Done);
    }
    return abortController.abort();
  };

  const clearChat = async () => {
    setClearingChat(true);
    if (
      appStateContext?.state.currentChat?.id &&
      appStateContext?.state.isCosmosDBAvailable.cosmosDB
    ) {
      let response = await historyClear(appStateContext?.state.currentChat.id);
      if (!response.ok) {
        setErrorMsg({
          title: "Error clearing current chat",
          subtitle:
            "Please try again. If the problem persists, please contact the site administrator.",
        });
        toggleErrorDialog();
      } else {
        appStateContext?.dispatch({
          type: "DELETE_CURRENT_CHAT_MESSAGES",
          payload: appStateContext?.state.currentChat.id,
        });
        appStateContext?.dispatch({
          type: "UPDATE_CHAT_HISTORY",
          payload: appStateContext?.state.currentChat,
        });
        setActiveCitation(undefined);
        setIsCitationPanelOpen(false);
        setMessages([]);
      }
    }
    setClearingChat(false);
  };

  const newChat = () => {
    setProcessMessages(messageStatus.Processing);
    setMessages([]);
    setIsCitationPanelOpen(false);
    setActiveCitation(undefined);
    appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: null });
    setProcessMessages(messageStatus.Done);
  };

  const stopGenerating = () => {
    abortFuncs.current.forEach((a) => a.abort());
    setShowLoadingMessage(false);
    setIsLoading(false);
  };

  const listenToMic = async (question: any, conversationId?: string) => {
    setIsLoading(true);
    setShowLoadingMessage(true);
    const abortController = new AbortController();
    abortFuncs.current.unshift(abortController);
    try {
      const recognizePayload = {
        conversation_id: conversationId || undefined,
        messages: [
          {
            id: uuid(),
            role: "user",
            content: question,
            date: new Date().toISOString(),
          },
        ],
      };

      let conversation: Conversation | null | undefined = conversationId
        ? appStateContext?.state?.currentChat
        : {
            id: uuid(),
            title: question,
            messages: [],
            date: new Date().toISOString(),
          };

      addMessageToConversation(conversation, recognizePayload.messages[0]);
      const response = await recognizeApi(
        recognizePayload,
        abortController.signal
      );
      let data = await response.json();
      setIsLoading(false);
      setShowLoadingMessage(false);
      data.text_content = removeDocumentReferenceFromString(data.text_content);
      data.voice_content = removeDocumentReferenceFromString(
        data.voice_content
      );
      // Sync the conversation ID returned by the backend (Cosmos-created) so
      // subsequent text chat messages can continue in the same conversation.
      const historyMeta = data.history_metadata;
      if (historyMeta?.conversation_id && conversation) {
        conversation.id = historyMeta.conversation_id;
        if (historyMeta.title && !conversationId) {
          conversation.title = historyMeta.title;
        }
      }
      addMessageToConversation(conversation, {
        id: uuid(),
        role: "assistant",
        content: data.text_content,
        date: new Date().toISOString(),
      });
      textToSpeech(data);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
      setShowLoadingMessage(false);
      abortFuncs.current = abortFuncs.current.filter(
        (a) => a !== abortController
      );
      setProcessMessages(messageStatus.Done);
    }

    return abortController.abort();
  };

  const textToSpeech = async (data: any) => {
    // Stop any currently playing speech before starting new one
    if (synthesizerRef.current) {
      synthesizerRef.current.close();
      synthesizerRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    setSynthesizer(null);
    // Show the stop button immediately — before the async token fetch
    setIsSpeaking(true);

    let token: string;
    let region: string;
    try {
      const result = await getSpeechToken();
      token = result.token;
      region = result.region;
    } catch (e) {
      console.error("Failed to get speech token:", e);
      setIsSpeaking(false);
      return;
    }

    const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(token, region);
    // Route synthesis output to a silent pull-stream so the SDK does NOT play
    // through the default speaker.  We play the returned audioData ourselves
    // via an HTML5 Audio element so we get a reliable `ended` event.
    const silentOutput = SpeechSDK.AudioOutputStream.createPullStream();
    const silentAudioConfig = SpeechSDK.AudioConfig.fromStreamOutput(silentOutput);
    const newSynthesizer = new SpeechSDK.SpeechSynthesizer(speechConfig, silentAudioConfig);
    synthesizerRef.current = newSynthesizer;
    setSynthesizer(newSynthesizer);

    newSynthesizer.speakSsmlAsync(
      data.voice_content,
      function (result: any) {
        newSynthesizer.close();
        synthesizerRef.current = null;
        setSynthesizer(null);

        if (
          result.reason === SpeechSDK.ResultReason.SynthesizingAudioCompleted &&
          result.audioData
        ) {
          // Play via HTML5 Audio so isSpeaking stays true until playback ends
          const blob = new Blob([result.audioData as ArrayBuffer], { type: "audio/wav" });
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          audioRef.current = audio;

          const cleanup = () => {
            setIsSpeaking(false);
            URL.revokeObjectURL(url);
            if (audioRef.current === audio) audioRef.current = null;
          };
          audio.onended = cleanup;
          audio.onerror = cleanup;
          audio.play().catch(cleanup);
        } else {
          console.error("Speech synthesis canceled: ", result.errorDetails);
          setIsSpeaking(false);
        }
      },
      function (err: any) {
        console.error("Speech synthesis error: ", err);
        newSynthesizer.close();
        synthesizerRef.current = null;
        setSynthesizer(null);
        setIsSpeaking(false);
      }
    );
  };

  const stopSpeech = () => {
    if (synthesizerRef.current) {
      synthesizerRef.current.close();
      synthesizerRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    setSynthesizer(null);
    setIsSpeaking(false);
  };

  useEffect(() => {
    if (appStateContext?.state.currentChat) {
      setMessages(appStateContext.state.currentChat.messages);
    } else {
      setMessages([]);
    }
  }, [appStateContext?.state.currentChat]);

  useLayoutEffect(() => {
    const saveToDB = async (messages: ChatMessage[], id: string) => {
      const response = await historyUpdate(messages, id);
      return response;
    };

    if (
      appStateContext &&
      appStateContext.state.currentChat &&
      processMessages === messageStatus.Done
    ) {
      if (appStateContext.state.isCosmosDBAvailable.cosmosDB) {
        if (!appStateContext?.state.currentChat?.messages) {
          console.error("Failure fetching current chat state.");
          return;
        }
        const noContentError = appStateContext.state.currentChat.messages.find(
          (m) => m.role === ERROR
        );

        if (!noContentError?.content.includes(NO_CONTENT_ERROR)) {
          saveToDB(
            appStateContext.state.currentChat.messages,
            appStateContext.state.currentChat.id
          )
            .then((res) => {
              if (!res.ok) {
                let errorMessage =
                  "An error occurred. Answers can't be saved at this time. If the problem persists, please contact the site administrator.";
                let errorChatMsg: ChatMessage = {
                  id: uuid(),
                  role: ERROR,
                  content: errorMessage,
                  date: new Date().toISOString(),
                };
                if (!appStateContext?.state.currentChat?.messages) {
                  let err: Error = {
                    ...new Error(),
                    message: "Failure fetching current chat state.",
                  };
                  throw err;
                }
                setMessages([
                  ...appStateContext?.state.currentChat?.messages,
                  errorChatMsg,
                ]);
              }
              return res as Response;
            })
            .catch((err) => {
              console.error("Error: ", err);
              let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
              };
              return errRes;
            });
        }
      } else {
      }
      appStateContext?.dispatch({
        type: "UPDATE_CHAT_HISTORY",
        payload: appStateContext.state.currentChat,
      });
      setMessages(appStateContext.state.currentChat.messages);
      setProcessMessages(messageStatus.NotRunning);
    }
  }, [processMessages]);

  useEffect(() => {
    if (AUTH_ENABLED !== undefined) getUserInfoList();
  }, [AUTH_ENABLED]);

  useLayoutEffect(() => {
    chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [showLoadingMessage, processMessages]);

  const onShowCitation = (citation: Citation) => {
    setActiveCitation(citation);
    setIsCitationPanelOpen(true);
  };

  const onViewSource = (citation: Citation) => {
    if (citation.url && !citation.url.includes("blob.core")) {
      window.open(citation.url, "_blank");
    }
  };

  const parseCitationFromMessage = (message: ChatMessage) => {
    if (message?.role && message?.role === "tool") {
      try {
        const toolMessage = JSON.parse(message.content) as ToolMessageContent;
        return toolMessage.citations;
      } catch {
        return [];
      }
    }
    return [];
  };

  const disabledButton = () => {
    return (
      isLoading ||
      (messages && messages.length === 0) ||
      clearingChat ||
      appStateContext?.state.chatHistoryLoadingState ===
        ChatHistoryLoadingState.Loading
    );
  };
  const baseUrl = window.location.href;
  return (
    <div className={styles.container} role="main">
      {showAuthMessage ? (
        <Stack className={styles.chatEmptyState}>
          <ShieldLockRegular
            className={styles.chatIcon}
            style={{ color: "darkorange", height: "200px", width: "200px" }}
          />
          <h1 className={styles.chatEmptyStateTitle}>
            Authentication Not Configured
          </h1>
          <h2 className={styles.chatEmptyStateSubtitle}>
            This app does not have authentication configured. Please add an
            identity provider by finding your app in the
            <a href="https://portal.azure.com/" target="_blank">
              {" "}
              Azure Portal{" "}
            </a>
            and following
            <a
              href="https://learn.microsoft.com/en-us/azure/app-service/scenario-secure-app-authentication-app-service#3-configure-authentication-and-authorization"
              target="_blank"
            >
              {" "}
              these instructions
            </a>
            .
          </h2>
          <h2
            className={styles.chatEmptyStateSubtitle}
            style={{ fontSize: "20px" }}
          >
            <strong>
              Authentication configuration takes a few minutes to apply.{" "}
            </strong>
          </h2>
          <h2
            className={styles.chatEmptyStateSubtitle}
            style={{ fontSize: "20px" }}
          >
            <strong>
              If you deployed in the last 10 minutes, please wait and reload the
              page after 10 minutes.
            </strong>
          </h2>
        </Stack>
      ) : (
        <Stack horizontal className={styles.chatRoot}>
          <div className={styles.chatContainer}>
            <Stack
              enableScopedSelectors
              horizontal
              horizontalAlign="space-between"
              className={styles.chatHeader}
            >
              <span className={styles.headerTitle}>
                <img src={QDBLogoChat} aria-hidden="true" />
                <span>
                  Ask <b>Sanad</b>
                </span>
                {messages.length > 0 && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={newChat}
                    aria-label="Clear session"
                  >
                    <img width={15} src={Refresh} aria-hidden="true" />
                  </div>
                )}
              </span>
              {/* <div style={{ display: "flex", alignContent: "center" }}>
                <img
                  src={CancelIcon}
                  aria-hidden="true"
                  width="30px"
                  onClick={handleCloseChat}
                />
              </div> */}
            </Stack>
            {!messages || messages.length < 1 ? (
              <div
                className={styles.chatMessageStream}
                style={{ marginBottom: isLoading ? "40px" : "0px" }}
                role="log"
              >
                <div className={styles.welcomeMessage}>
                  <img width={32} src={Jassim} aria-hidden="true" />
                  <div className={styles.welcomeMessageTitle}>
                    Welcome to Qatar Development Bank !
                  </div>
                  <div>
                    We're here to assist you 24/7. Whether you have questions
                    about your account, need help with business, or just seeking
                    financial advice, type your query below and we'll get right
                    on it. How can we serve you today?
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={styles.chatMessageStream}
                style={{ marginBottom: isLoading ? "40px" : "0px" }}
                role="log"
              >
                {messages.map((answer, index) => (
                  <>
                    {answer.role === "user" ? (
                      <div className={styles.chatMessageUser} tabIndex={0}>
                        <div className={styles.chatMessageUserMessage}>
                          {answer.content}
                        </div>
                      </div>
                    ) : answer.role === "assistant" ? (
                      <div className={styles.chatMessageGpt}>
                        <Answer
                          answer={{
                            answer: answer.content,
                            citations: parseCitationFromMessage(
                              messages[index - 1]
                            ),
                          }}
                          isDoneProcessing={!isLoading}
                          onCitationClicked={(c) => onShowCitation(c)}
                        />
                      </div>
                    ) : answer.role === "error" ? (
                      <div className={styles.chatMessageError}>
                        <Stack
                          horizontal
                          className={styles.chatMessageErrorContent}
                        >
                          <img width={32} src={Jassim} aria-hidden="true" />
                          <span>Error</span>
                        </Stack>
                        <span className={styles.chatMessageErrorContent}>
                          {answer.content}
                        </span>
                      </div>
                    ) : null}
                  </>
                ))}
                {showLoadingMessage && (
                  <>
                    <div className={styles.chatMessageGpt}>
                      <Answer
                        answer={{
                          answer: "Generating answer...",
                          citations: [],
                        }}
                        isDoneProcessing={!isLoading}
                        onCitationClicked={() => null}
                      />
                    </div>
                  </>
                )}
                <div ref={chatMessageStreamEnd} />
              </div>
            )}

            <Stack horizontal className={styles.chatInput}>
              {false && (
                <Stack
                  horizontal
                  className={styles.stopGeneratingContainer}
                  role="button"
                  aria-label="Stop generating"
                  tabIndex={0}
                  onClick={stopGenerating}
                  onKeyDown={(e) =>
                    e.key === "Enter" || e.key === " " ? stopGenerating() : null
                  }
                >
                  <SquareRegular
                    className={styles.stopGeneratingIcon}
                    aria-hidden="true"
                  />
                  <span
                    className={styles.stopGeneratingText}
                    aria-hidden="true"
                  >
                    Stop generating
                  </span>
                </Stack>
              )}
              <Dialog
                hidden={hideErrorDialog}
                onDismiss={handleErrorDialogClose}
                dialogContentProps={errorDialogContentProps}
                modalProps={modalProps}
              ></Dialog>
              <QuestionInput
                clearOnSend
                placeholder="Type your question!"
                disabled={false}
                isSpeaking={isSpeaking}
                onSend={(question, id) => {
                  appStateContext?.state.isCosmosDBAvailable?.cosmosDB
                    ? makeApiRequestWithCosmosDB(question, id)
                    : makeApiRequestWithoutCosmosDB(question, id);
                }}
                conversationId={
                  appStateContext?.state.currentChat?.id
                    ? appStateContext?.state.currentChat?.id
                    : undefined
                }
                onListen={(question, id) => {
                  listenToMic(question, id);
                }}
                onStopSpeech={stopSpeech}
              />
            </Stack>
          </div>
          {messages &&
            messages.length > 0 &&
            isCitationPanelOpen &&
            activeCitation && (
              <Stack.Item
                className={styles.citationPanel}
                tabIndex={0}
                role="tabpanel"
                aria-label="Citations Panel"
              >
                <Stack
                  aria-label="Citations Panel Header Container"
                  horizontal
                  className={styles.citationPanelHeaderContainer}
                  horizontalAlign="space-between"
                  verticalAlign="center"
                >
                  <span
                    aria-label="Citations"
                    className={styles.citationPanelHeader}
                  >
                    Citations
                  </span>
                  <IconButton
                    iconProps={{ iconName: "Cancel" }}
                    aria-label="Close citations panel"
                    onClick={() => setIsCitationPanelOpen(false)}
                  />
                </Stack>
                <h5
                  className={styles.citationPanelTitle}
                  tabIndex={0}
                  title={
                    activeCitation.url &&
                    !activeCitation.url.includes("blob.core")
                      ? activeCitation.url
                      : activeCitation.title ?? ""
                  }
                  onClick={() => onViewSource(activeCitation)}
                >
                  {activeCitation.title}
                </h5>
                <div tabIndex={0}>
                  <ReactMarkdown
                    linkTarget="_blank"
                    className={styles.citationPanelContent}
                    children={DOMPurify.sanitize(activeCitation.content, {
                      ALLOWED_TAGS: XSSAllowTags,
                    })}
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                  />
                </div>
              </Stack.Item>
            )}
          {appStateContext?.state.isChatHistoryOpen &&
            appStateContext?.state.isCosmosDBAvailable?.status !==
              CosmosDBStatus.NotConfigured && <ChatHistoryPanel />}
        </Stack>
      )}
    </div>
  );
};

export default Chat;
